import React from 'react';
import { Checkbox, Tooltip } from '@mui/material';
import { formatDateDDMMYYYY } from '~/helpers/utils.ts';
import { stylePadding, showSorterTooltip } from '../styles.ts';
import {
  getStatusText,
  getStatusTooltip,
  getDenyReason,
  getSelectedKeyValue,
  getSafeUniqueOptions,
  safeStringSorter,
  OPTION_ALL,
  SUBMIT_STATUS_FILTER_OPTIONS,
} from '../utils/utils.ts';
import { ComplexServiceCheckbox } from '../components/ComplexServiceCheckbox.tsx';
import { RadioGroup } from '../components/RadioGroup.tsx';
import {
  ComplexServiceCheckboxValue,
  FilterDropdownProps,
  TSubmitColumnType,
  TSubmitRecord,
} from '../types.ts';
import { HiddenField } from '../components/HiddenField.tsx';
import { TooltipCheckbox } from '../components/TooltipCheckbox.tsx';
import { DocumentActions } from '../components/DocumentActions.tsx';

interface IColumnProps {
  content: TSubmitRecord[];
  handleDocumentClick: (id: string, documentId: string) => void;
  onCheckboxChange: (recordKey: string | number, fieldName: string, checked: boolean) => void;
}

export const getSubmitColumns = ({
  content = [],
  handleDocumentClick,
  onCheckboxChange,
}: IColumnProps): TSubmitColumnType[] => [
  {
    title: 'Електронна пошта',
    dataIndex: 'email',
    key: 'email',
    render: (email: unknown, record: TSubmitRecord) =>
      record.isHidden ? <HiddenField /> : String(email ?? ''),
    sorter: safeStringSorter('email'),
    showSorterTooltip,
  },
  {
    title: 'Номер телефону',
    dataIndex: 'phone',
    key: 'phone',
    render: (phone: unknown, record: TSubmitRecord) =>
      record.isHidden ? <HiddenField /> : String(phone ?? ''),
    sorter: safeStringSorter('phone'),
    showSorterTooltip,
  },
  {
    title: 'РНОКПП',
    dataIndex: 'identificationNumber',
    key: 'identificationNumber',
    sorter: safeStringSorter('identificationNumber'),
    showSorterTooltip,
  },
  {
    title: 'Дата подачі',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (createdAt: unknown) => formatDateDDMMYYYY(String(createdAt ?? '')),
    sorter: safeStringSorter('createdAt'),
    showSorterTooltip,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: FilterDropdownProps) => {
      const currentValue = getSelectedKeyValue(selectedKeys);

      return (
        <RadioGroup
          value={currentValue}
          options={[
            OPTION_ALL,
            ...getSafeUniqueOptions(content, (item) => formatDateDDMMYYYY(item.createdAt)),
          ]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSelectedKeys(val === OPTION_ALL.value ? [] : [val]);
            confirm();
          }}
        />
      );
    },
    onFilter: (value: string | number, record: TSubmitRecord) =>
      value === undefined || formatDateDDMMYYYY(record.createdAt) === value,
  },
  {
    title: <TooltipCheckbox tooltipText="Надання Банком фінансових послуг" text="1" />,
    dataIndex: 'financialServices',
    key: 'financialServices',
    onCell: () => stylePadding,
    render: (val: unknown, record: TSubmitRecord) => (
      <ComplexServiceCheckbox
        value={val as ComplexServiceCheckboxValue | null}
        recordKey={record.key}
        fieldName="financialServices"
        onChange={onCheckboxChange}
      />
    ),
  },
  {
    title: <TooltipCheckbox tooltipText="Отримання карти – перепустки" text="2" />,
    dataIndex: 'cardAccess',
    key: 'cardAccess',
    onCell: () => stylePadding,
    render: (_: unknown, record: TSubmitRecord) => (
      <Checkbox
        checked={!!record.cardAccess?.selected}
        onChange={(e) => {
          if (onCheckboxChange) {
            onCheckboxChange(record.key, 'cardAccess', e.target.checked);
          }
        }}
      />
    ),
  },
  {
    title: <TooltipCheckbox tooltipText="Встановлення ділових відносин" text="3" />,
    dataIndex: 'businessRelationship',
    key: 'businessRelationship',
    onCell: () => stylePadding,
    render: (val: unknown, record: TSubmitRecord) => (
      <ComplexServiceCheckbox
        value={val as ComplexServiceCheckboxValue | null}
        recordKey={record.key}
        fieldName="businessRelationship"
        onChange={onCheckboxChange}
      />
    ),
  },
  {
    title: <TooltipCheckbox tooltipText="Отримання даних з БКІ" text="4" />,
    dataIndex: 'creditBureauData',
    key: 'creditBureauData',
    onCell: () => stylePadding,
    render: (_: unknown, record: TSubmitRecord) => (
      <Checkbox
        checked={!!record.creditBureauData?.selected}
        onChange={(e) => {
          if (onCheckboxChange) {
            onCheckboxChange(record.key, 'creditBureauData', e.target.checked);
          }
        }}
      />
    ),
  },
  {
    title: 'Статус заявки',
    dataIndex: 'status',
    key: 'status',
    onCell: () => stylePadding,
    render: (status: unknown, record: TSubmitRecord) => {
      const normalizedStatus = String(status ?? '');
      const { denyType, denyReason } = record;
      const parsedDenyType = denyType !== undefined ? Number(denyType) : undefined;
      const normalizedDenyType = Number.isNaN(parsedDenyType) ? undefined : parsedDenyType;

      const tooltipText =
        normalizedStatus === 'DENIED'
          ? getDenyReason(normalizedDenyType, denyReason)
          : getStatusTooltip(normalizedStatus, 'submit');

      return (
        <Tooltip title={tooltipText}>
          <span>{getStatusText(normalizedStatus, 'submit')}</span>
        </Tooltip>
      );
    },
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: FilterDropdownProps) => {
      const currentValue = getSelectedKeyValue(selectedKeys);

      return (
        <RadioGroup
          value={currentValue}
          options={[OPTION_ALL, ...SUBMIT_STATUS_FILTER_OPTIONS]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSelectedKeys(val === OPTION_ALL.value ? [] : [val]);
            confirm();
          }}
        />
      );
    },
    onFilter: (value: string | number, record: TSubmitRecord) =>
      value === undefined || record.status === value,
  },
  {
    title: 'Дата оновлення',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: (updatedAt: unknown) => formatDateDDMMYYYY(String(updatedAt ?? '')),
    sorter: safeStringSorter('updatedAt'),
    showSorterTooltip,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: FilterDropdownProps) => {
      const currentValue = getSelectedKeyValue(selectedKeys);

      return (
        <RadioGroup
          value={currentValue}
          options={[
            OPTION_ALL,
            ...getSafeUniqueOptions(content, (item) => formatDateDDMMYYYY(item.updatedAt)),
          ]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSelectedKeys(val === OPTION_ALL.value ? [] : [val]);
            confirm();
          }}
        />
      );
    },
    onFilter: (value: string | number, record: TSubmitRecord) =>
      value === undefined || formatDateDDMMYYYY(record.updatedAt) === value,
  },
  {
    title: 'Логін ініціатора',
    dataIndex: 'initiatedByFullName',
    key: 'initiatedByFullName',
    sorter: safeStringSorter('initiatedByFullName'),
    showSorterTooltip,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: FilterDropdownProps) => {
      const currentValue = getSelectedKeyValue(selectedKeys);

      return (
        <RadioGroup
          value={currentValue}
          options={[
            OPTION_ALL,
            ...getSafeUniqueOptions(content, (item) => item.initiatedByFullName ?? ''),
          ]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSelectedKeys(val === OPTION_ALL.value ? [] : [val]);
            confirm();
          }}
        />
      );
    },
    onFilter: (value: string | number, record: TSubmitRecord) =>
      value === undefined || record.initiatedByFullName === value,
  },
  {
    title: 'Документ',
    key: 'actions',
    dataIndex: 'actions',
    width: 120,
    align: 'center' as const,
    render: (_: unknown, record: TSubmitRecord) => (
      <DocumentActions record={record} handleDocumentClick={handleDocumentClick} />
    ),
  },
];
